import axios from 'axios';

/**
 * Helpers for turning backend error responses into user-facing messages.
 *
 * The registration endpoints respond with:
 *   { success: false, error: string, details?: ZodIssue[] }
 * where `details` names the exact field(s) that failed validation. Historically
 * the forms surfaced only the generic `error` string ("Invalid registration
 * data"), leaving users with no idea what to fix. These helpers read `details`
 * so the failing field is shown instead.
 */

type ValidationDetail = { path?: (string | number)[]; message?: string };

const FIELD_LABELS: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  participantType: 'Participant type',
  organization: 'Organization',
  role: 'Job position',
  linkedinUrl: 'LinkedIn',
  websiteUrl: 'Website',
  bio: 'Bio',
  accelerationInterests: 'Technology interests',
  acknowledgements: 'Acknowledgements',
  rank: 'Rank',
  branchOfService: 'Service branch',
};

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

/** The top-level field name from a Zod issue path (ignores nested indices). */
function topField(detail: ValidationDetail): string {
  if (!Array.isArray(detail.path)) return '';
  const first = detail.path.find((p) => typeof p === 'string');
  return typeof first === 'string' ? first : '';
}

function validationDetails(err: unknown): ValidationDetail[] {
  if (!axios.isAxiosError(err)) return [];
  const data = err.response?.data as { details?: unknown } | undefined;
  return Array.isArray(data?.details) ? (data!.details as ValidationDetail[]) : [];
}

/**
 * Build a readable message from an error response. When field-level validation
 * details are present, each is rendered as "Field: message"; otherwise the
 * backend's generic error string (or a fallback) is used.
 */
export function getApiErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const details = validationDetails(err);
  if (details.length > 0) {
    const messages = details
      .map((d) => {
        const field = topField(d);
        const msg = d.message ?? 'is invalid';
        return field ? `${fieldLabel(field)}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (messages.length > 0) return messages.join(' · ');
  }

  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: unknown } | undefined;
    if (typeof data?.error === 'string') return data.error;
    if (data?.error && typeof data.error === 'object' && 'message' in data.error) {
      return String((data.error as { message: unknown }).message);
    }
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/**
 * Map a validation error response onto a { field: message } object suitable for
 * merging into a form's per-field error state. Only top-level fields are mapped.
 */
export function getApiFieldErrors(err: unknown): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const d of validationDetails(err)) {
    const field = topField(d);
    if (field && !errors[field]) errors[field] = d.message ?? 'This field is invalid';
  }
  return errors;
}
